"use client"

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import Image from "next/image"
import {Paperclip, X} from "lucide-react"
import {toast} from "sonner"
import {Button} from "@/components/ui/button"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {LinkPreviewList} from "@/app/components/editor/link-preview/LinkPreviewList"
import {FileDropOverlay} from "@/app/components/FileDropOverlay"
import {Loader} from "@/app/components/loader"
import {useConfluence, useJira, useLinear, useNotion} from "@/hooks/queries/use-integrations"
import {externalLinkToIssueAttachmentMetadata, extractAllLinks, getExternalLinkKey, issueAttachmentMetadataForExternalLinks, previewToIssueAttachmentMetadata, type LinkProvider} from "@/lib/link-preview-utils"
import {signAttachment} from "@/hooks/attachment-presign"
import {imageAttachmentPresignAPI} from "@/app/apiPaths"
import {DOCUMENT_VALIDATION, IMAGE_VALIDATION, SPREADSHEET_VALIDATION, isAllowedImageExtension} from "@/lib/validation"
import {useFileUpload} from "@/hooks/use-file-upload"
import {
  AttachmentPresignRequest,
  CreateSignedLinkResponse,
} from "@/pb/api/devplan/services/web/company"
import type {
  ExternalLink, ExternalLinkPreview, IssueAttachmentMetadata,
} from "@/pb/api/devplan/types/attachments"
import type {AttachmentInfo} from "@/hooks/use-unified-file-upload"
import type {CreationAttachmentsPayload} from "@/app/components/projects/creation/AttachmentsAndIssueLinksInput"
import {cn} from "@/lib/utils"
import {getAttachmentIcon} from "@/app/components/attachmentIcon"
import {AttachmentType} from "@/pb/api/devplan/types/attachments"

type FileKind = "image" | "document" | "spreadsheet" | "unsupported"

interface PendingFile {
  /** Local-only id so we can dedupe state updates and find the row to remove on completion. */
  localId: string
  file: File
  kind: FileKind
  generation: number
}

export interface UseOptimisticAttachmentsOptions {
  companyId: number
  description: string
  disabled?: boolean
  compactUploadTrigger?: boolean
  uploadTriggerVariant?: "outline" | "ghost"
}

export interface UseOptimisticAttachmentsReturn {
  /** Files currently uploading — rendered with a spinner. */
  pendingFiles: PendingFile[]
  /** Uploaded image URLs. */
  imageUrls: string[]
  /** Uploaded document/spreadsheet attachments. */
  attachments: AttachmentInfo[]
  removePending: (localId: string) => void
  removeImage: (index: number) => void
  removeAttachment: (id: string) => void
  detectedLinks: ExternalLink[]
  issueAttachmentsByKey: Record<string, IssueAttachmentMetadata>
  handleRemoveLink: (link: ExternalLink) => void
  handlePreviewLoaded: (preview: ExternalLinkPreview, originalLink: ExternalLink) => void
  handlePaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void
  dragActive: boolean
  dragHandlers: {
    onDragEnter: (event: React.DragEvent) => void
    onDragLeave: (event: React.DragEvent) => void
    onDragOver: (event: React.DragEvent) => void
    onDrop: (event: React.DragEvent) => void
  }
  uploadTrigger: React.ReactNode
  /** True while any file is still uploading; consumers gate submit on this. */
  uploadsInFlight: boolean
  /** Build the create-project payload from the currently uploaded artifacts. */
  buildPayload: () => CreationAttachmentsPayload
  /** Clear all local attachment/link state. In-flight uploads from older generations are ignored. */
  reset: () => void
}

/**
 * Optimistic attachments composer.
 *
 * Files are queued and shown in the UI the instant they're dropped (with a
 * spinner overlay) while the actual upload runs in the background. When the
 * upload settles, the pending chip is swapped for a normal uploaded chip;
 * on failure the pending entry is removed and a toast surfaces the reason.
 *
 * This component intentionally mirrors `AttachmentsAndIssueLinksInput`'s
 * external surface (uses the same `CreationAttachmentsPayload` shape) so the
 * project-intake dialog can swap composers without otherwise diverging.
 */
export function useOptimisticAttachments({
  companyId,
  description,
  disabled,
  compactUploadTrigger,
  uploadTriggerVariant = "outline",
}: UseOptimisticAttachmentsOptions): UseOptimisticAttachmentsReturn {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [imageAttachmentIds, setImageAttachmentIds] = useState<string[]>([])
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([])
  const [dragActive, setDragActive] = useState(false)
  const dragCounter = useRef(0)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const localIdCounter = useRef(0)
  const uploadGeneration = useRef(0)
  const cancelledPendingIds = useRef<Set<string>>(new Set())

  // Link recognition — mirrors AttachmentsAndIssueLinksInput.
  const {info: jiraInfo, loading: jiraLoading} = useJira(companyId)
  const {info: linearInfo, loading: linearLoading} = useLinear(companyId)
  const {info: notionInfo, loading: notionLoading} = useNotion(companyId)
  const {info: confluenceInfo, loading: confluenceLoading} = useConfluence(companyId)
  const hasJira = !jiraLoading && jiraInfo !== undefined
  const hasLinear = !linearLoading && linearInfo !== undefined
  const hasNotion = !notionLoading && notionInfo !== undefined
  const hasConfluence = !confluenceLoading && confluenceInfo !== undefined
  const activeLinkProviders = useMemo<LinkProvider[]>(() => {
    const providers: LinkProvider[] = ["google-drive"]
    if (hasJira) providers.push("jira")
    if (hasLinear) providers.push("linear", "linear-project")
    if (hasNotion) providers.push("notion")
    if (hasConfluence) providers.push("confluence")
    return providers
  }, [hasConfluence, hasJira, hasLinear, hasNotion])
  const [detectedLinks, setDetectedLinks] = useState<ExternalLink[]>([])
  const [dismissedLinkKeys, setDismissedLinkKeys] = useState<Set<string>>(() => new Set())
  const [issueAttachmentsByKey, setIssueAttachmentsByKey] =
    useState<Record<string, IssueAttachmentMetadata>>({})

  useEffect(() => {
    if (activeLinkProviders.length === 0) {
      setDetectedLinks([])
      return
    }
    const links = extractAllLinks(description || "", activeLinkProviders)
    const activeKeys = new Set(links.map(getExternalLinkKey))
    setDismissedLinkKeys(prev => {
      const next = new Set([...prev].filter(key => activeKeys.has(key)))
      return next.size === prev.size ? prev : next
    })
    setDetectedLinks(links.filter(link => !dismissedLinkKeys.has(getExternalLinkKey(link))))
  }, [activeLinkProviders, description, dismissedLinkKeys])

  useEffect(() => {
    setIssueAttachmentsByKey(prev => {
      const next: Record<string, IssueAttachmentMetadata> = {}
      for (const link of detectedLinks) {
        const key = getExternalLinkKey(link)
        const fallback = externalLinkToIssueAttachmentMetadata(link)
        if (prev[key] || fallback) {
          next[key] = prev[key] ?? fallback!
        }
      }
      return next
    })
  }, [detectedLinks])

  const handleRemoveLink = useCallback((link: ExternalLink) => {
    const key = getExternalLinkKey(link)
    setDismissedLinkKeys(prev => new Set(prev).add(key))
    setDetectedLinks(prev => prev.filter(item => getExternalLinkKey(item) !== key))
    setIssueAttachmentsByKey(prev => {
      const next = {...prev}
      delete next[key]
      return next
    })
  }, [])

  const handlePreviewLoaded = useCallback((preview: ExternalLinkPreview, originalLink: ExternalLink) => {
    const meta = previewToIssueAttachmentMetadata(preview)
    const link = preview.link
    if (!meta || !link?.provider || !link.identifier) return
    setIssueAttachmentsByKey(prev => ({
      ...prev,
      [getExternalLinkKey(originalLink)]: meta,
    }))
  }, [])

  const classifyFile = useCallback((file: File): FileKind => {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const type = file.type.toLowerCase()
    const noType = !type || type === 'application/octet-stream'
    const isImage = type.startsWith('image/') || (noType && isAllowedImageExtension(ext))
    if (isImage) return "image"
    // Validation constants are typed as `readonly [...]` tuples, so widen
    // before .includes() to satisfy the literal-string check on `ext`/`type`.
    const docExts: readonly string[] = DOCUMENT_VALIDATION.allowedExtensions
    const docTypes: readonly string[] = DOCUMENT_VALIDATION.allowedTypes
    const sheetExts: readonly string[] = SPREADSHEET_VALIDATION.allowedExtensions
    const sheetTypes: readonly string[] = SPREADSHEET_VALIDATION.allowedTypes
    if (docExts.includes(ext) || docTypes.includes(type)) return "document"
    if (sheetExts.includes(ext) || sheetTypes.includes(type)) return "spreadsheet"
    return "unsupported"
  }, [])

  // Presign router and uploader. Same routing as useUnifiedFileUpload.
  const urlSigner = useCallback(
    async (extension: string, fileType: string, fileName: string, fileSize: number) => {
      if (fileType.startsWith('image/')) {
        const resp = await fetch(imageAttachmentPresignAPI(companyId), {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(AttachmentPresignRequest.create({
            fileExtension: extension, fileType, fileSize, originalFileName: fileName,
          })),
        })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({error: 'Image presign failed'}))
          throw new Error(err.error || 'Image presign failed')
        }
        const data = CreateSignedLinkResponse.fromJSON(await resp.json())
        return {fullPath: data.id, signedUrl: data.url}
      }
      return signAttachment(companyId, extension, fileType, fileName, fileSize)
    },
    [companyId],
  )

  const allowedMimeTypes = useMemo(
    () => [
      ...IMAGE_VALIDATION.allowedTypes,
      ...DOCUMENT_VALIDATION.allowedTypes,
      ...SPREADSHEET_VALIDATION.allowedTypes,
    ],
    [],
  )
  const allowedExtensions = useMemo(
    () => [
      ...IMAGE_VALIDATION.allowedExtensions,
      ...DOCUMENT_VALIDATION.allowedExtensions,
      ...SPREADSHEET_VALIDATION.allowedExtensions,
    ],
    [],
  )

  const {uploadFile} = useFileUpload(
    urlSigner,
    {allowedTypes: allowedMimeTypes, allowedExtensions},
    {noSuccessToast: true},
  )

  const startUpload = useCallback(async (entry: PendingFile) => {
    try {
      const path = await uploadFile(entry.file)
      setPendingFiles(prev => prev.filter(p => p.localId !== entry.localId))
      if (!path || entry.generation !== uploadGeneration.current || cancelledPendingIds.current.has(entry.localId)) {
        cancelledPendingIds.current.delete(entry.localId)
        return
      }
      if (entry.kind === "image") {
        setImageUrls(prev => [...prev, path])
        setImageAttachmentIds(prev => [...prev, imageAttachmentIdFromPath(path)])
      } else {
        setAttachments(prev => [...prev, {id: path, fileName: entry.file.name}])
      }
    } catch (err) {
      setPendingFiles(prev => prev.filter(p => p.localId !== entry.localId))
      toast.error(`Failed to upload ${entry.file.name}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }, [uploadFile])

  const validateAndEnqueue = useCallback((incoming: File[]) => {
    const accepted: PendingFile[] = []
    for (const file of incoming) {
      const kind = classifyFile(file)
      if (kind === "unsupported") {
        toast.error(`Unsupported file: ${file.name}`)
        continue
      }
      const limit =
        kind === "image" ? IMAGE_VALIDATION.maxSize
          : kind === "spreadsheet" ? SPREADSHEET_VALIDATION.maxSize
            : DOCUMENT_VALIDATION.maxSize
      if (file.size > limit) {
        const mb = (limit / 1024 / 1024).toFixed(0)
        toast.error(`${file.name} exceeds the ${mb}MB limit`)
        continue
      }
      localIdCounter.current += 1
      accepted.push({localId: `local-${localIdCounter.current}`, file, kind, generation: uploadGeneration.current})
    }
    if (accepted.length === 0) return
    setPendingFiles(prev => [...prev, ...accepted])
    // Fire all uploads in parallel; each one removes itself from the
    // pending list when settled.
    for (const entry of accepted) {
      void startUpload(entry)
    }
  }, [classifyFile, startUpload])

  const dragHandlers = useMemo(() => ({
    onDragEnter: (event: React.DragEvent) => {
      event.preventDefault(); event.stopPropagation()
      dragCounter.current++
      if (event.dataTransfer.types.includes('Files')) setDragActive(true)
    },
    onDragLeave: (event: React.DragEvent) => {
      event.preventDefault(); event.stopPropagation()
      dragCounter.current = Math.max(0, dragCounter.current - 1)
      if (dragCounter.current === 0) setDragActive(false)
    },
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault(); event.stopPropagation()
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault(); event.stopPropagation()
      dragCounter.current = 0
      setDragActive(false)
      if (disabled) return
      const files = Array.from(event.dataTransfer.files ?? [])
      if (files.length > 0) validateAndEnqueue(files)
    },
  }), [disabled, validateAndEnqueue])

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return
    const items = Array.from(event.clipboardData.items)
    const fileItems = items.filter(it => it.kind === 'file')
    if (fileItems.length === 0) return
    event.preventDefault()
    const files = fileItems
      .map(it => it.getAsFile())
      .filter((f): f is File => f !== null)
    if (files.length > 0) validateAndEnqueue(files)
  }, [disabled, validateAndEnqueue])

  const onFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ""
    if (files.length > 0) validateAndEnqueue(files)
  }, [validateAndEnqueue])

  const acceptedFileTypes = useMemo(() => [
      ...IMAGE_VALIDATION.allowedTypes,
      ...IMAGE_VALIDATION.allowedExtensions.map(e => `.${e}`),
      ...DOCUMENT_VALIDATION.allowedTypes,
      ...DOCUMENT_VALIDATION.allowedExtensions.map(e => `.${e}`),
      ...SPREADSHEET_VALIDATION.allowedTypes,
      ...SPREADSHEET_VALIDATION.allowedExtensions.map(e => `.${e}`),
    ].join(',')
  , [])

  const uploadTrigger = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        multiple
        onChange={onFileInputChange}
        className="hidden"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size={compactUploadTrigger ? "icon-sm" : "icon"}
            variant={uploadTriggerVariant}
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className={cn(compactUploadTrigger && "size-3.5", "dark:text-white")}/>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Attach files (images, PDF, DOCX, Markdown, CSV, XLSX)
        </TooltipContent>
      </Tooltip>
    </>
  )

  const removePending = useCallback((localId: string) => {
    cancelledPendingIds.current.add(localId)
    setPendingFiles(prev => prev.filter(p => p.localId !== localId))
  }, [])

  const removeImage = useCallback((index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index))
    setImageAttachmentIds(prev => prev.filter((_, i) => i !== index))
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id))
  }, [])

  const buildPayload = useCallback((): CreationAttachmentsPayload => {
    const latestLinks = extractAllLinks(description || "", activeLinkProviders)
      .filter(link => !dismissedLinkKeys.has(getExternalLinkKey(link)))
    return {
      imageUrls,
      attachmentIds: [...attachments.map(a => a.id), ...imageAttachmentIds],
      issueAttachments: issueAttachmentMetadataForExternalLinks(latestLinks, issueAttachmentsByKey),
    }
  }, [activeLinkProviders, description, dismissedLinkKeys, imageUrls, attachments, imageAttachmentIds, issueAttachmentsByKey])

  const reset = useCallback(() => {
    uploadGeneration.current += 1
    cancelledPendingIds.current.clear()
    dragCounter.current = 0
    setPendingFiles([])
    setImageUrls([])
    setImageAttachmentIds([])
    setAttachments([])
    setDragActive(false)
    setDetectedLinks([])
    setDismissedLinkKeys(new Set())
    setIssueAttachmentsByKey({})
  }, [])

  return {
    pendingFiles,
    imageUrls,
    attachments,
    removePending,
    removeImage,
    removeAttachment,
    detectedLinks,
    issueAttachmentsByKey,
    handleRemoveLink,
    handlePreviewLoaded,
    handlePaste,
    dragActive,
    dragHandlers,
    uploadTrigger,
    uploadsInFlight: pendingFiles.length > 0,
    buildPayload,
    reset,
  }
}

function imageAttachmentIdFromPath(path: string): string {
  try {
    const pathname = path.startsWith("http") ? new URL(path).pathname : path
    const fileName = pathname.split("/").filter(Boolean).pop() ?? path
    return fileName.replace(/\.[^/.]+$/, "")
  } catch {
    const fileName = path.split("/").filter(Boolean).pop() ?? path
    return fileName.replace(/\.[^/.]+$/, "")
  }
}

/**
 * Renders the combined chip list: pending (uploading, with spinner) and
 * already-uploaded attachments. Order: pending first so the chip the user
 * just dropped appears at the front of the row.
 */
export function OptimisticFilePreviewList({
  pendingFiles,
  imageUrls,
  attachments,
  onRemovePending,
  onRemoveImage,
  onRemoveAttachment,
}: {
  pendingFiles: PendingFile[]
  imageUrls: string[]
  attachments: AttachmentInfo[]
  onRemovePending: (localId: string) => void
  onRemoveImage: (index: number) => void
  onRemoveAttachment: (id: string) => void
}) {
  // Object URLs for the pending image previews. Track per-localId so swapping
  // out an entry doesn't accidentally revoke a URL still in use.
  const pendingImageUrls = useMemo(() => {
    const map = new Map<string, string>()
    for (const entry of pendingFiles) {
      if (entry.kind === "image") {
        map.set(entry.localId, URL.createObjectURL(entry.file))
      }
    }
    return map
  }, [pendingFiles])

  useEffect(() => {
    return () => {
      for (const url of pendingImageUrls.values()) URL.revokeObjectURL(url)
    }
  }, [pendingImageUrls])

  if (pendingFiles.length === 0 && imageUrls.length === 0 && attachments.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-2">
      {pendingFiles.map(entry => {
        const url = pendingImageUrls.get(entry.localId)
        return (
          <div key={entry.localId} className="relative inline-block m-1">
            {url ? (
              <div className="relative">
                <Image
                  src={url}
                  className="rounded-lg object-cover opacity-60"
                  alt={entry.file.name}
                  height={50}
                  width={50}
                  unoptimized
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader className="size-4 text-white drop-shadow"/>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-sm opacity-70">
                <Loader className="size-3 text-muted-foreground"/>
                <span className="max-w-[150px] truncate text-foreground">{entry.file.name}</span>
              </div>
            )}
            <Button
              type="button"
              onClick={() => onRemovePending(entry.localId)}
              aria-label={`Cancel ${entry.file.name}`}
              variant="ghost"
              size="icon"
              className={chipRemoveButtonClass}
            >
              <X className="size-3" aria-hidden="true"/>
            </Button>
          </div>
        )
      })}

      {imageUrls.map((url, index) => (
        <div key={`img-${url}-${index}`} className="relative inline-block m-1">
          <a href={url} target="_blank" className="cursor-pointer">
            <Image src={url} className="rounded-lg" alt="image" height={50} width={50} unoptimized/>
          </a>
          <Button
            type="button"
            onClick={() => onRemoveImage(index)}
            aria-label={`Remove image ${index + 1}`}
            variant="ghost"
            size="icon"
            className={chipRemoveButtonClass}
          >
            <X className="size-3" aria-hidden="true"/>
          </Button>
        </div>
      ))}

      {attachments.map(attachment => (
        <div key={`doc-${attachment.id}`} className="relative inline-block m-1">
          <div className="flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-sm">
            <span className="[&_svg]:size-4">{getAttachmentIcon(AttachmentType.DOCUMENT, attachment.fileName)}</span>
            <span className="max-w-[150px] truncate text-foreground">{attachment.fileName}</span>
          </div>
          <Button
            type="button"
            onClick={() => onRemoveAttachment(attachment.id)}
            aria-label={`Remove ${attachment.fileName}`}
            variant="ghost"
            size="icon"
            className={chipRemoveButtonClass}
          >
            <X className="size-3" aria-hidden="true"/>
          </Button>
        </div>
      ))}
    </div>
  )
}

const chipRemoveButtonClass = cn(
  "absolute end-1 top-1 translate-x-1/2 -translate-y-1/2 rounded-full",
  "border-[3px] border-[#f4f4f4] bg-black p-[2px] text-white",
  "dark:border-token-bg-tertiary dark:bg-white dark:text-black",
  "hover:bg-black/80 dark:hover:bg-white/80 h-auto w-auto",
)

export {LinkPreviewList, FileDropOverlay}
